import React, { useState, useEffect } from 'react';
import {
  Modal, Form, Input, Select, message, Row, Col,
  Alert, Avatar, Descriptions, Tag, Badge, Divider,
  Tooltip, Popconfirm, DatePicker, Dropdown, Menu,
  Statistic, Card, Button, Space, Grid
} from 'antd';
import {
  PlusOutlined, SearchOutlined, EditOutlined,
  DeleteOutlined, EyeOutlined, DownloadOutlined,
  ReloadOutlined, FilterOutlined, MoreOutlined,
  CheckCircleOutlined, ClockCircleOutlined,
  CloseCircleOutlined, DollarCircleOutlined,
  CalendarOutlined, LineChartOutlined, ShopOutlined,
  UserOutlined, TruckOutlined, FileTextOutlined,
  DashboardOutlined, CheckOutlined, DollarOutlined,
  ShoppingOutlined, FileDoneOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from '../../utils/axios';
import AddVentes from './modals/AddVentes';
import ReusableTable from '../../components/ReusableTable';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;
const { Countdown } = Statistic;

const Ventes = () => {
  const [ventes, setVentes] = useState([]);
  const [filteredVentes, setFilteredVentes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedVente, setSelectedVente] = useState(null);
  const [actionType, setActionType] = useState('add');
  const [stats, setStats] = useState({
    totalDemandesEnAttente: 0,
    totalCAValidé: 0,
    totalQuantitéValidée: 0,
    dateDerniereValidation: null
  });
  const [exportLoading, setExportLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const screens = useBreakpoint();

  const statusConfig = {
    'Validée': {
      color: '#10b981',
      bgColor: '#ecfdf5',
      icon: <CheckCircleOutlined />,
      borderColor: '#10b981'
    },
    'Payée': {
      color: '#3b82f6',
      bgColor: '#eff6ff',
      icon: <DollarCircleOutlined />,
      borderColor: '#3b82f6'
    },
    'En attente': {
      color: '#f59e0b',
      bgColor: '#fffbeb',
      icon: <ClockCircleOutlined />,
      borderColor: '#f59e0b'
    },
    'Annulée': {
      color: '#ef4444',
      bgColor: '#fef2f2',
      icon: <CloseCircleOutlined />,
      borderColor: '#ef4444'
    }
  };

  // Fonction pour charger les ventes
  const fetchVentes = async (page = 1, limit = 10) => {
    try {
      setLoading(true);

      const queryParams = {
        page,
        limit,
        search: searchText,
        statut: statusFilter,
      };

      // Ajouter les filtres de date
      if (dateRange && dateRange[0] && dateRange[1]) {
        queryParams.startDate = dateRange[0].format('YYYY-MM-DD');
        queryParams.endDate = dateRange[1].format('YYYY-MM-DD');
      }

      const response = await axios.get('/api/ventes', { params: queryParams });

      if (response.data.success) {
        const ventesData = response.data.data.ventes || [];
       
        const statsCalculated = calculateStats(ventesData);
        setStats(statsCalculated);

        const formattedVentes = ventesData.map(vente => ({
          ...vente,
          id: vente.id || vente._id,
          dateFormatted: vente.date ? dayjs(vente.date).format('DD/MM/YYYY') : '',
          montantFormatted: vente.montant ?
            Number(vente.montant).toLocaleString('fr-FR', {
              style: 'currency',
              currency: 'XOF',
              minimumFractionDigits: 0,
            }) : '0 XOF'
        }));
       
        setVentes(formattedVentes);
        setFilteredVentes(formattedVentes);
      } else {
        message.error(response.data.message || 'Erreur lors du chargement des ventes');
      }
    } catch (error) {
      console.error('Erreur lors du chargement des ventes:', error);
      message.error('Erreur lors du chargement des ventes');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (ventesData) => {
    const ventesEnAttente = ventesData.filter(v => v.statut === 'En attente');
    const ventesValidees = ventesData.filter(v => v.statut === 'Validée' || v.statut === 'Payée');
   
    const totalDemandesEnAttente = ventesEnAttente.length;
    const totalCAValidé = ventesValidees.reduce((sum, v) => sum + (parseFloat(v.montant) || 0), 0);
    const totalQuantitéValidée = ventesValidees.reduce((sum, v) => sum + (parseFloat(v.quantite) || 0), 0);
   
    const ventesValideesAvecDates = ventesValidees
      .filter(v => v.dateFacture || v.createdAt)
      .map(v => ({
        ...v,
        date: v.dateFacture || v.createdAt
      }));
   
    ventesValideesAvecDates.sort((a, b) => new Date(b.date) - new Date(a.date));
   
    const dateDerniereValidation = ventesValideesAvecDates.length > 0
      ? ventesValideesAvecDates[0].date
      : null;

    return {
      totalDemandesEnAttente,
      totalCAValidé,
      totalQuantitéValidée,
      dateDerniereValidation
    };
  };

  useEffect(() => {
    fetchVentes(currentPage, pageSize);
  }, []);

  // Appliquer les filtres
  const applyFilters = () => {
    fetchVentes(1, pageSize);
  };

  // Gestion des filtres
  const handleFilterChange = (field, value) => {
    if (field === 'statut') {
      setStatusFilter(value);
    } else if (field === 'dateRange') {
      setDateRange(value);
    }
  };

  // Gestion de la recherche
  const handleSearch = () => {
    applyFilters();
  };

  const handleReset = () => {
    setSearchText('');
    setStatusFilter('');
    setDateRange(null);
    fetchVentes(1, pageSize);
  };

  const handleDateChange = (dates) => {
    setDateRange(dates);
    if (dates && dates[0] && dates[1]) {
      fetchVentes(1, pageSize);
    }
  };

  // Actions CRUD
  const handleAdd = () => {
    setSelectedVente(null);
    setActionType('add');
    setIsModalVisible(true);
  };

  const handleEdit = (vente) => {
    const isLocked = vente.statut === 'Validée' || vente.statut === 'Payée';
    if (isLocked) {
      message.warning('Cette vente ne peut pas être modifiée');
      return;
    }
    setSelectedVente(vente);
    setActionType('edit');
    setIsModalVisible(true);
  };

  const handleView = (vente) => {
    setSelectedVente(vente);
    setActionType('view');
    setIsModalVisible(true);
  };

  const handleDelete = async (vente) => {
    // Confirmation supplémentaire pour les ventes payées
    const isPaid = vente.statut === 'Payée';
 
    Modal.confirm({
      title: 'Confirmer la suppression',
      content: isPaid
        ? 'Cette vente est marquée comme payée. Êtes-vous sûr de vouloir la supprimer ?'
        : 'Êtes-vous sûr de vouloir supprimer cette vente ?',
      okText: 'Oui',
      okType: 'danger',
      cancelText: 'Non',
      onOk: async () => {
        try {
          const response = await axios.delete(`/api/ventes/${vente.id}`);

          if (response.data.success) {
            message.success('Vente supprimée avec succès');
            fetchVentes(currentPage, pageSize);
          } else {
            message.error(response.data.message);
          }
        } catch (error) {
          console.error('Erreur lors de la suppression:', error);
          message.error(error.response?.data?.message || 'Erreur lors de la suppression');
        }
      }
    });
  };

  const handleUpdateStatus = async (vente) => {
    Modal.confirm({
      title: 'Changer le statut de la vente',
      content: (
        <Select
          defaultValue={vente.statut}
          style={{ width: '100%', marginTop: '10px' }}
          onChange={(value) => handleConfirmStatus(vente.id, value)}
        >
          <Option value="En attente">En attente</Option>
          <Option value="Validée">Validée</Option>
          <Option value="Payée">Payée</Option>
          <Option value="Annulée">Annulée</Option>
        </Select>
      ),
      onOk: () => {},
      onCancel: () => {},
    });
  };

  const handleConfirmStatus = async (id, statut) => {
    try {
      const response = await axios.patch(`/api/ventes/${id}/status`, { statut });

      if (response.data.success) {
        message.success('Statut mis à jour avec succès');
        fetchVentes(currentPage, pageSize);
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      message.error('Erreur lors de la mise à jour du statut');
    }
  };

  // Exportation
  const handleExportSingle = (vente) => {
    const data = {
      'N° Facture': vente.numeroFacture,
      'Désignation': vente.designation,
      'Client': vente.client,
      'Client ID': vente.clientId || 'N/A',
      'Quantité (tonnes)': vente.quantite,
      'Montant TTC': vente.statut === 'Validée' || vente.statut === 'Payée'
        ? Number(vente.montant).toLocaleString('fr-FR', {
            style: 'currency',
            currency: 'XOF',
            minimumFractionDigits: 0,
          })
        : 'Non validé',
      'Chauffeur': vente.chauffeur || 'Non spécifié',
      'Chauffeur ID': vente.chauffeurId || 'N/A',
      'Statut': vente.statut,
      'Date': vente.date,
    };

    const csv = Object.keys(data).map(key => `${key}: ${data[key]}`).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vente_${vente.numeroFacture || 'export'}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    message.success('Vente exportée avec succès');
  };

  const handleExportAll = async () => {
    try {
      setExportLoading(true);

      const response = await axios.get('/api/ventes', {
        params: { limit: 10000 }
      });

      if (response.data.success) {
        const ventes = response.data.data.ventes || [];

        const headers = ['N° Facture', 'Désignation', 'Client', 'Client ID', 'Quantité (tonnes)', 'Montant TTC', 'Chauffeur', 'Chauffeur ID', 'Statut', 'Date'];
        const csvRows = [
          headers.join(','),
          ...ventes.map(v => [
            `"${v.numeroFacture || ''}"`,
            `"${v.designation || ''}"`,
            `"${v.client || ''}"`,
            `"${v.clientId || ''}"`,
            v.quantite || 0,
            v.statut === 'Validée' || v.statut === 'Payée' ? (v.montant || 0) : 'Non validé',
            `"${v.chauffeur || ''}"`,
            `"${v.chauffeurId || ''}"`,
            `"${v.statut || ''}"`,
            `"${v.date || ''}"`
          ].join(','))
        ];

        const csv = csvRows.join('\n');
        const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.href = url;
        link.setAttribute('download', `export_ventes_${dayjs().format('YYYYMMDD_HHmmss')}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        message.success(`${ventes.length} ventes exportées avec succès`);
      }
    } catch (error) {
      console.error('Erreur lors de l\'exportation:', error);
      message.error('Erreur lors de l\'exportation');
    } finally {
      setExportLoading(false);
    }
  };

  const handleModalSuccess = () => {
    setIsModalVisible(false);
    fetchVentes(currentPage, pageSize);
  };

  // Configuration des colonnes
  const columns = [
    {
      title: 'N° FACTURE',
      dataIndex: 'numeroFacture',
      key: 'numeroFacture',
      width: 140,
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ padding: '6px', backgroundColor: '#e6f4ff', borderRadius: '6px' }}>
            <FileTextOutlined style={{ color: '#1890ff', fontSize: '12px' }} />
          </div>
          <span style={{
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '600',
            color: '#1890ff'
          }}>
            {text || 'N/A'}
          </span>
        </div>
      )
    },
    {
      title: 'DÉSIGNATION',
      dataIndex: 'designation',
      key: 'designation',
      width: 180,
      render: (text) => (
        <Tooltip title={text}>
          <div style={{
            fontWeight: '500',
            color: '#333',
            fontSize: '13px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {text}
          </div>
        </Tooltip>
      )
    },
    {
      title: 'CLIENT',
      dataIndex: 'client',
      key: 'client',
      width: 140,
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ padding: '4px', backgroundColor: '#f5f5f5', borderRadius: '50%' }}>
            <UserOutlined style={{ color: '#666', fontSize: '12px' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: '500', color: '#333', fontSize: '12px' }}>
              {text}
            </span>
            {record.clientId && (
              <span style={{ fontSize: '11px', color: '#999' }}>
                ID: {record.clientId.substring(0, 8)}...
              </span>
            )}
          </div>
        </div>
      )
    },
    {
      title: 'QUANTITÉ',
      dataIndex: 'quantite',
      key: 'quantite',
      width: 120,
      render: (text) => (
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '6px 12px',
          borderRadius: '6px',
          background: 'linear-gradient(to right, #e6f7ff, #f0f5ff)'
        }}>
          <span style={{
            fontWeight: '700',
            color: '#1890ff',
            fontSize: '12px'
          }}>
            {Number(text).toLocaleString('fr-FR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
          </span>
          <span style={{
            fontSize: '11px',
            color: '#1890ff',
            fontWeight: '500'
          }}>
            t
          </span>
        </div>
      ),
      cellStyle: { textAlign: 'center' }
    },
    {
      title: 'MONTANT',
      dataIndex: 'montant',
      key: 'montant',
      width: 140,
      render: (text, record) => {
        if (record.statut !== 'Validée' && record.statut !== 'Payée') {
          return <div style={{ color: '#999', fontStyle: 'italic', fontSize: '12px' }}>-</div>;
        }
        return (
          <div style={{
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            padding: '8px',
            borderRadius: '6px',
            background: 'linear-gradient(to right, #f6ffed, #f0f5ff)'
          }}>
            <div style={{
              fontWeight: '700',
              color: '#52c41a',
              fontSize: '12px'
            }}>
              {Number(text).toLocaleString('fr-FR', {
                style: 'currency',
                currency: 'XOF',
                minimumFractionDigits: 0,
              })}
            </div>
            <div style={{
              fontSize: '11px',
              color: '#52c41a',
              fontWeight: '500',
              marginTop: '2px'
            }}>
              TTC
            </div>
          </div>
        );
      },
      cellStyle: { textAlign: 'right' }
    },
    {
      title: 'CHAUFFEUR',
      dataIndex: 'chauffeur',
      key: 'chauffeur',
      width: 140,
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ padding: '4px', backgroundColor: '#fff7e6', borderRadius: '50%' }}>
            <TruckOutlined style={{ color: '#fa8c16', fontSize: '12px' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ color: '#333', fontSize: '12px' }}>
              {text || 'Non spécifié'}
            </span>
            {record.chauffeurId && (
              <span style={{ fontSize: '11px', color: '#999' }}>
                ID: {record.chauffeurId.substring(0, 8)}...
              </span>
            )}
          </div>
        </div>
      )
    },
    {
      title: 'STATUT',
      dataIndex: 'statut',
      key: 'statut',
      width: 130,
      render: (statut) => {
        const config = statusConfig[statut] || {
          color: '#6b7280',
          bgColor: '#f3f4f6',
          borderColor: '#d1d5db'
        };
       
        return (
          <div
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border"
            style={{
              background: config.bgColor,
              borderColor: config.borderColor,
              borderWidth: '1px'
            }}
          >
            <span style={{ color: config.color }}>
              {config.icon}
            </span>
            <span
              className="font-semibold text-xs"
              style={{ color: config.color }}
            >
              {statut}
            </span>
          </div>
        );
      },
      cellStyle: { textAlign: 'center' }
    },
    {
      title: 'DATE',
      dataIndex: 'date',
      key: 'date',
      width: 110,
      render: (date) => (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <div style={{
            fontWeight: '600',
            color: '#333',
            fontSize: '13px'
          }}>
            {date ? dayjs(date).format('DD/MM') : 'N/A'}
          </div>
          <div style={{ fontSize: '11px', color: '#666' }}>
            {date ? dayjs(date).format('YYYY') : ''}
          </div>
        </div>
      )
    }
  ];

  // Données paginées
  const paginatedData = filteredVentes.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Options de statut pour ReusableTable
  const statusOptions = Object.entries(statusConfig).map(([value, config]) => ({
    value,
    label: value,
    color: config.color,
    backgroundColor: config.bgColor,
    borderColor: config.borderColor,
    icon: config.icon
  }));

  // Configuration des filtres
  const tableFilters = [
    {
      field: 'statut',
      placeholder: 'Statut',
      value: statusFilter,
      options: [
        { value: 'En attente', label: 'En attente' },
        { value: 'Validée', label: 'Validée' },
        { value: 'Payée', label: 'Payée' },
        { value: 'Annulée', label: 'Annulée' }
      ],
      width: '140px'
    }
  ];

  // ACTIONS PERSONNALISÉES POUR LE TABLEAU - UN SEUL EyeOutlined
  const customActions = [
    {
      tooltip: 'Exporter',
      icon: <DownloadOutlined />,
      onClick: handleExportSingle,
      color: '#722ed1'
    }
  ];

  // Actions de statut personnalisées
  const handleStatusAction = (vente) => {
    const isLocked = vente.statut === 'Payée' || vente.statut === 'Annulée';
    if (!isLocked) {
      handleUpdateStatus(vente);
    }
  };

  const statusActions = [
    {
      tooltip: 'Changer statut',
      icon: <CheckOutlined />,
      onClick: handleStatusAction,
      color: '#fa8c16',
      condition: (record) => record.statut !== 'Payée' && record.statut !== 'Annulée'
    }
  ];

  // Filtres supplémentaires
  const extraFilters = (
    <RangePicker
      format="DD/MM/YYYY"
      onChange={handleDateChange}
      value={dateRange}
      style={{ width: '100%' }}
      size="large"
      placeholder={['Date début', 'Date fin']}
    />
  );

  // Boutons supplémentaires
  const extraButtons = (
    <>
      <Button
        icon={<DownloadOutlined />}
        onClick={handleExportAll}
        loading={exportLoading}
        size="large"
        style={{
          borderColor: '#d9d9d9',
          color: '#666'
        }}
      >
        Exporter
      </Button>
      <Button
        icon={<ReloadOutlined />}
        onClick={handleReset}
        size="large"
        style={{
          borderColor: '#d9d9d9',
          color: '#666'
        }}
      >
        Réinitialiser
      </Button>
    </>
  );

  // Contenu d'en-tête personnalisé avec statistiques
  const headerContent = (
    <div style={{ marginBottom: '20px' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        paddingBottom: '16px',
        borderBottom: '1px solid #e8e8e8'
      }}>
        <div>
          <h1 style={{
            margin: '0 0 8px 0',
            fontSize: '24px',
            fontWeight: '600',
            color: '#333',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <DashboardOutlined style={{ color: '#5fa5e7' }} />
            Gestion des Ventes
          </h1>
          <div style={{
            fontSize: '14px',
            color: '#666',
            display: 'flex',
            gap: '24px',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ClockCircleOutlined style={{ color: '#fa8c16' }} />
              <span><strong>{stats.totalDemandesEnAttente || 0}</strong> En attente</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <DollarOutlined style={{ color: '#52c41a' }} />
              <span><strong>
                {stats.totalCAValidé.toLocaleString('fr-FR', {
                  style: 'currency',
                  currency: 'XOF',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                }).replace('XOF', '')}
              </strong> XOF</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ShoppingOutlined style={{ color: '#1890ff' }} />
              <span><strong>{stats.totalQuantitéValidée.toFixed(1)}</strong> tonnes</span>
            </div>
            <div style={{
              backgroundColor: '#5fa5e7',
              color: 'white',
              padding: '4px 12px',
              borderRadius: '12px',
              fontWeight: '500'
            }}>
              Total: {filteredVentes.length}
            </div>
          </div>
        </div>
       
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
          size="large"
          style={{
            backgroundColor: '#5fa5e7',
            borderColor: '#5fa5e7',
            padding: '0 24px',
            height: '40px',
            fontWeight: '500',
            borderRadius: '6px',
            boxShadow: '0 2px 8px rgba(95, 165, 231, 0.3)'
          }}
        >
          Nouvelle Vente
        </Button>
      </div>

      {/* Statistiques détaillées */}
      <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card
            style={{
              border: 'none',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #fffbeb 0%, #fff7e6 100%)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                padding: '12px',
                backgroundColor: '#fff7e6',
                borderRadius: '8px'
              }}>
                <ClockCircleOutlined style={{ color: '#fa8c16', fontSize: '20px' }} />
              </div>
              <div>
                <div style={{ color: '#666', fontSize: '12px', fontWeight: '500' }}>
                  En attente
                </div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#333' }}>
                  {stats.totalDemandesEnAttente || 0}
                  <span style={{ fontSize: '14px', color: '#666', marginLeft: '4px' }}>
                    demandes
                  </span>
                </div>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card
            style={{
              border: 'none',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                padding: '12px',
                backgroundColor: '#d1fae5',
                borderRadius: '8px'
              }}>
                <CheckCircleOutlined style={{ color: '#10b981', fontSize: '20px' }} />
              </div>
              <div>
                <div style={{ color: '#666', fontSize: '12px', fontWeight: '500' }}>
                  Chiffre d'affaires
                </div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#333' }}>
                  {stats.totalCAValidé > 0 ? (
                    <>
                      {stats.totalCAValidé.toLocaleString('fr-FR', {
                        style: 'currency',
                        currency: 'XOF',
                        minimumFractionDigits: 0,
                      }).replace('XOF', '')}
                      <span style={{ fontSize: '14px', color: '#666', marginLeft: '4px' }}>
                        XOF
                      </span>
                    </>
                  ) : '-'}
                </div>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card
            style={{
              border: 'none',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                padding: '12px',
                backgroundColor: '#dbeafe',
                borderRadius: '8px'
              }}>
                <LineChartOutlined style={{ color: '#3b82f6', fontSize: '20px' }} />
              </div>
              <div>
                <div style={{ color: '#666', fontSize: '12px', fontWeight: '500' }}>
                  Quantité validée
                </div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#333' }}>
                  {stats.totalQuantitéValidée > 0 ? (
                    <>
                      {stats.totalQuantitéValidée.toFixed(1)}
                      <span style={{ fontSize: '14px', color: '#666', marginLeft: '4px' }}>
                        tonnes
                      </span>
                    </>
                  ) : '-'}
                </div>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card
            style={{
              border: 'none',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                padding: '12px',
                backgroundColor: '#ede9fe',
                borderRadius: '8px'
              }}>
                <CalendarOutlined style={{ color: '#8b5cf6', fontSize: '20px' }} />
              </div>
              <div>
                <div style={{ color: '#666', fontSize: '12px', fontWeight: '500' }}>
                  Dernière validation
                </div>
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>
                  {stats.dateDerniereValidation
                    ? dayjs(stats.dateDerniereValidation).format('DD/MM/YYYY')
                    : 'Aucune'
                  }
                </div>
                {stats.dateDerniereValidation && (
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                    {dayjs(stats.dateDerniereValidation).format('HH:mm')}
                  </div>
                )}
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );

  return (
    <>
      <ReusableTable
        // Configuration de base
        title="Gestion des Ventes"
        data={paginatedData}
        columns={columns}
        loading={loading}
       
        // Pagination
        totalItems={filteredVentes.length}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={(page, size) => {
          setCurrentPage(page);
          if (size) setPageSize(size);
        }}
        onSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
       
        // Recherche et filtres
        searchText={searchText}
        onSearchChange={setSearchText}
        onSearch={handleSearch}
        filters={tableFilters}
        onFilterChange={handleFilterChange}
        extraFilters={extraFilters}
       
        // Actions
        onAdd={handleAdd}
        onRefresh={() => fetchVentes(currentPage, pageSize)}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onView={handleView}
       
        // Personnalisation
        addButtonText="Nouvelle Vente"
        searchPlaceholder="Rechercher par N° facture, client, désignation..."
        showStats={false} // On utilise notre propre header avec stats
        showEntriesInfo={true}
       
        // Colonnes spéciales
        showStatusColumn={false} // Nous avons notre propre colonne statut dans les columns
        statusOptions={statusOptions}
       
        // Configuration des boutons d'action
        actionButtons={['view', 'edit', 'delete']}
        customActions={[...customActions, ...statusActions]}
       
        // Personnalisation des couleurs
        primaryColor="#5fa5e7"
        borderColor="#5fa5e7"
       
        // Options supplémentaires
        extraButtons={extraButtons}
       
        // Contenu personnalisé
        headerContent={headerContent}
       
        // Titre du tableau
        tableTitle="Transactions commerciales"
       
        // Configuration spéciale
        showIdColumn={true}
        showActionColumn={true}
        showSearch={true}
        showFilters={true}
      />

      {/* Modal AddVentes */}
      <AddVentes
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSuccess={handleModalSuccess}
        vente={selectedVente}
        actionType={actionType}
      />

      {/* Modal de détail de vente */}
      {selectedVente && actionType === 'view' && (
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <FileTextOutlined style={{ color: '#5fa5e7', fontSize: '20px' }} />
              <span style={{ fontSize: '18px', fontWeight: '600' }}>Détails de la Vente</span>
            </div>
          }
          open={actionType === 'view'}
          onCancel={() => {
            setSelectedVente(null);
            setActionType('add');
          }}
          footer={[
            <Button
              key="close"
              onClick={() => {
                setSelectedVente(null);
                setActionType('add');
              }}
              size="large"
            >
              Fermer
            </Button>,
            <Button
              key="edit"
              type="primary"
              onClick={() => {
                handleEdit(selectedVente);
              }}
              size="large"
              style={{ backgroundColor: '#5fa5e7', borderColor: '#5fa5e7' }}
              icon={<EditOutlined />}
            >
              Modifier
            </Button>,
            <Button
              key="export"
              type="default"
              onClick={() => handleExportSingle(selectedVente)}
              size="large"
              icon={<DownloadOutlined />}
            >
              Exporter
            </Button>
          ]}
          width={700}
        >
          {selectedVente && (
            <div style={{ padding: '20px 0' }}>
              {/* Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '20px',
                padding: '20px',
                backgroundColor: '#f0f7ff',
                borderRadius: '8px',
                marginBottom: '24px'
              }}>
                <div style={{
                  padding: '16px',
                  backgroundColor: '#5fa5e7',
                  borderRadius: '12px'
                }}>
                  <FileTextOutlined style={{ color: 'white', fontSize: '32px' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <h2 style={{
                    margin: '0 0 8px 0',
                    fontSize: '24px',
                    fontWeight: '600',
                    color: '#333'
                  }}>
                    {selectedVente.designation}
                  </h2>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <Tag
                      color={statusConfig[selectedVente.statut]?.color}
                      style={{
                        padding: '6px 16px',
                        fontSize: '14px',
                        fontWeight: '500',
                        borderRadius: '20px'
                      }}
                      icon={statusConfig[selectedVente.statut]?.icon}
                    >
                      {selectedVente.statut}
                    </Tag>
                    <Tag
                      color="blue"
                      style={{
                        padding: '6px 16px',
                        fontSize: '14px',
                        fontWeight: '500',
                        borderRadius: '20px'
                      }}
                    >
                      N°: {selectedVente.numeroFacture}
                    </Tag>
                  </div>
                </div>
              </div>

              {/* Informations principales */}
              <Descriptions
                column={1}
                bordered
                size="middle"
                labelStyle={{
                  fontWeight: '600',
                  width: '200px',
                  backgroundColor: '#fafafa',
                  padding: '12px 16px'
                }}
                contentStyle={{
                  padding: '12px 16px',
                  backgroundColor: 'white'
                }}
                style={{
                  borderRadius: '8px',
                  overflow: 'hidden',
                  marginBottom: '24px'
                }}
              >
                <Descriptions.Item label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <UserOutlined />
                    Client
                  </div>
                }>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      backgroundColor: '#e6f7ff',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#1890ff'
                    }}>
                      {selectedVente.client}
                    </div>
                    {selectedVente.clientId && (
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        ID: {selectedVente.clientId}
                      </div>
                    )}
                  </div>
                </Descriptions.Item>
               
                <Descriptions.Item label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <TruckOutlined />
                    Chauffeur
                  </div>
                }>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      backgroundColor: '#fff7e6',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#fa8c16'
                    }}>
                      {selectedVente.chauffeur || 'Non spécifié'}
                    </div>
                    {selectedVente.chauffeurId && (
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        ID: {selectedVente.chauffeurId}
                      </div>
                    )}
                  </div>
                </Descriptions.Item>
               
                <Descriptions.Item label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <ShoppingOutlined />
                    Quantité
                  </div>
                }>
                  <div style={{
                    backgroundColor: '#f6ffed',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#52c41a',
                    border: '2px solid #b7eb8f'
                  }}>
                    {selectedVente.quantite} tonnes
                  </div>
                </Descriptions.Item>
               
                {(selectedVente.statut === 'Validée' || selectedVente.statut === 'Payée') && (
                  <Descriptions.Item label={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <DollarOutlined />
                      Montant
                    </div>
                  }>
                    <div style={{
                      backgroundColor: '#f0f5ff',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      fontSize: '20px',
                      fontWeight: '700',
                      color: '#2f54eb',
                      border: '2px solid #adc6ff'
                    }}>
                      {selectedVente.montantFormatted}
                    </div>
                  </Descriptions.Item>
                )}
               
                <Descriptions.Item label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CalendarOutlined />
                    Date
                  </div>
                }>
                  <div style={{
                    backgroundColor: '#fafafa',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    {selectedVente.dateFormatted}
                  </div>
                </Descriptions.Item>
              </Descriptions>

              {/* Informations supplémentaires */}
              {selectedVente.observations && (
                <Card
                  title="Observations"
                  style={{ marginBottom: '16px' }}
                >
                  <div style={{ color: '#7e7c7c', fontSize: '14px', lineHeight: '1.6' }}>
                    {selectedVente.observations}
                  </div>
                </Card>
              )}
            </div>
          )}
        </Modal>
      )}
    </>
  );
};

export default Ventes;
